import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import spacetime from "spacetime";
import { BananaApi } from "../api/BananaApi";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { BananaStats } from "../config/Types";
import { BananaType } from "../types/BananaType";
import { countryFlag } from "../utils/countryFlag";

const formatNumber = (value: number) => value.toLocaleString("en-US");

const BananaverseLivePage = () => {
  const [stats, setStats] = useState<BananaStats>({ totalSent: 0, totalForwarded: 0, totalSentBack: 0 });
  const [history, setHistory] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([BananaApi.getPublicBananaTransactions(), BananaApi.getBananaverseStats()])
      .then(([feed, totals]) => {
        setHistory(feed.bananaOperations || []);
        setStats(totals);
      })
      .catch((error) => console.error("Failed to load the Bananaverse:", error))
      .finally(() => setIsLoading(false));
  }, []);

  const journeys = useMemo(() => history
    .filter((entry) => entry.originCountry && entry.destinationCountry && entry.originCountry !== entry.destinationCountry)
    .slice(0, 4), [history]);
  const momentsOfJoy = stats.totalSent + stats.totalForwarded + stats.totalSentBack;

  return <>
    <title>Bananaverse Live | Joy Traveling Around the World</title>
    <meta name="description" content="See anonymous messages of love and joy traveling around the world through Tuttobanana." />
    <main className="min-h-screen bg-yellow-50 text-gray-900 overflow-hidden">
      <section className="relative bg-gray-950 text-white px-6 py-16 sm:px-12 sm:py-24">
        <div className="absolute -top-20 -left-20 h-64 w-64 rounded-full bg-yellow-400 opacity-10 blur-3xl"></div>
        <div className="absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-yellow-400 opacity-10 blur-3xl"></div>
        <div className="relative max-w-6xl mx-auto grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-sm font-black uppercase tracking-widest text-yellow-400">Bananaverse Live</p>
            <h1 className="mt-5 text-4xl sm:text-6xl font-black leading-tight">Love is moving.<br/><span className="text-yellow-400">Watch it travel.</span></h1>
            <p className="mt-6 max-w-xl text-lg sm:text-xl leading-relaxed text-gray-300">Every glowing path began with one person deciding to say something meaningful. Together, those tiny gifts are creating a more connected world.</p>
            <Link to="/banana-stand" className="mt-8 inline-flex items-center rounded-full bg-yellow-400 px-7 py-3 font-black text-gray-950 shadow-lg transition hover:bg-yellow-300">Send some joy →</Link>
          </div>
          <div className="relative mx-auto flex h-72 w-72 items-center justify-center sm:h-96 sm:w-96" aria-label="Anonymous joy traveling around the world">
            <div className="absolute inset-4 rounded-full border border-yellow-400 bg-gray-900 opacity-70 shadow-2xl"></div>
            <div className="absolute inset-10 rounded-full border border-dashed border-yellow-300 rotate-12 opacity-50"></div>
            <div className="absolute inset-16 rounded-full border border-dashed border-white -rotate-12 opacity-25"></div>
            <div className="relative text-9xl sm:text-[12rem] drop-shadow-2xl">🌍</div>
            <span className="absolute left-10 top-20 h-3 w-3 animate-pulse rounded-full bg-yellow-300"></span>
            <span className="absolute right-12 top-28 h-3 w-3 animate-pulse rounded-full bg-yellow-300"></span>
            <span className="absolute bottom-20 left-28 h-3 w-3 animate-pulse rounded-full bg-yellow-300"></span>
            <span className="absolute right-2 top-8 rotate-12 text-4xl">🍌</span>
            <span className="absolute bottom-8 left-0 -rotate-12 text-3xl">🍌</span>
          </div>
        </div>
      </section>

      <section className="relative z-10 -mt-7 px-6 sm:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[[formatNumber(momentsOfJoy), "moments of joy"], [formatNumber(stats.totalSent), "bananas started"], [formatNumber(stats.totalForwarded), "carried forward"], [formatNumber(stats.totalSentBack), "replies sent back"]].map(([value, label]) =>
            <div key={label} className="rounded-2xl border border-yellow-200 bg-white p-5 text-center shadow-lg">
              <p className="text-3xl sm:text-4xl font-black">{value}</p><p className="mt-1 text-xs sm:text-sm font-bold uppercase tracking-wide text-gray-500">{label}</p>
            </div>)}
        </div>
      </section>

      <section className="px-6 py-16 sm:px-12 sm:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <p className="text-sm font-black uppercase tracking-widest text-yellow-600">The human signal</p>
            <h2 className="mt-3 text-3xl sm:text-5xl font-black">What people are putting into the world</h2>
            <p className="mx-auto mt-4 max-w-2xl text-gray-600">Real messages shared publicly by their senders, shown without names, handles, recipients, cities, or private links.</p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {[[formatNumber(stats.totalSent), "bananas tossed"], [formatNumber(stats.countriesReached || 0), "countries reached"], [formatNumber(stats.internationalConnections || 0), "cross-border journeys"]].map(([value, label]) =>
              <div key={label} className="rounded-2xl border-2 border-gray-900 bg-yellow-400 p-5 text-center shadow-lg">
                <p className="text-3xl font-black">{value}</p><p className="mt-1 text-sm font-bold">{label}</p>
              </div>)}
          </div>

          {journeys.length > 0 && <div className="mt-8 flex gap-3 overflow-x-auto pb-3">
            {journeys.map((journey) => <div key={`route-${journey.id}`} className="min-w-fit rounded-full border border-gray-200 bg-white px-5 py-3 font-bold shadow-sm">
              {countryFlag(journey.originCountry)} {journey.originCountry} <span className="px-2 text-yellow-500">→</span> {countryFlag(journey.destinationCountry)} {journey.destinationCountry} 🍌
            </div>)}
          </div>}

          <div className="mt-12 columns-1 gap-5 sm:columns-2 lg:columns-3">
            {isLoading && <div className="mb-5 break-inside-avoid rounded-2xl bg-white p-8 shadow"><LoadingSpinner label="Gathering joy from around the world..." size="md" /></div>}
            {!isLoading && history.map((entry, index) => <article key={entry.id} className={`mb-5 min-w-0 break-inside-avoid overflow-hidden rounded-2xl border p-6 shadow-sm ${index % 4 === 0 ? "border-yellow-300 bg-yellow-100" : "border-gray-200 bg-white"}`}>
              <div className="flex items-center justify-between gap-3">
                {BananaType.getBananaTypeByKey(entry.bananaType)?.img
                  ? <img className="h-12 w-12 object-contain" src={BananaType.getBananaTypeByKey(entry.bananaType)?.img} alt="" />
                  : <span className="text-3xl">🍌</span>}
                <span className="rounded-full bg-gray-950 px-3 py-1 text-[10px] font-black uppercase tracking-wide text-white">Shared publicly</span>
              </div>
              <blockquote className="mt-5 max-w-full whitespace-pre-wrap break-words text-xl font-bold leading-relaxed [overflow-wrap:anywhere]">“{entry.message}”</blockquote>
              <div className="mt-6 max-w-full break-words border-t border-gray-200 pt-4 text-xs font-semibold text-gray-500 [overflow-wrap:anywhere]">
                {entry.originCountry && entry.destinationCountry
                  ? <span>{countryFlag(entry.originCountry)} {entry.originCountry} → {countryFlag(entry.destinationCountry)} {entry.destinationCountry}</span>
                  : <span>{entry.country ? `Shared from ${countryFlag(entry.country)} ${entry.country}` : "Shared with the world 🌍"}</span>}
                {entry.createdAt && <span> · {spacetime(entry.createdAt).unixFmt("MMM YYYY")}</span>}
              </div>
            </article>)}
          </div>

          {!isLoading && !history.length && <div className="mt-12 rounded-3xl border-2 border-dashed border-yellow-400 bg-white p-10 text-center">
            <div className="text-5xl">🍌</div><h3 className="mt-4 text-2xl font-black">The next message could begin here.</h3>
          </div>}

          <div className="mt-14 rounded-3xl bg-gray-950 px-6 py-10 text-center text-white sm:px-12">
            <p className="text-yellow-400 font-black uppercase tracking-widest">Tiny gift. Big feeling.</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black">Someone in your world needs to hear it.</h2>
            <p className="mx-auto mt-3 max-w-xl text-gray-300">Say something meaningful. Send it anywhere. Give the feeling a chance to travel.</p>
            <Link to="/banana-stand" className="mt-7 inline-flex rounded-full bg-yellow-400 px-8 py-3 font-black text-gray-950 hover:bg-yellow-300">Toss a banana—it’s free →</Link>
          </div>

          <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-relaxed text-gray-500">Historical messages shown here came from bananas their senders designated public. New messages appear only after a sender explicitly publishes an anonymous copy. Personal names, account handles, recipients, cities, and private delivery links are not displayed.</p>
        </div>
      </section>
    </main>
  </>;
};

export default BananaverseLivePage;
